'use client';

import {
  faHeart as faHeartRegular,
  faSpinnerThird,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useState } from 'react';

import { GearImageCarousel } from '@/modules/players/v3/components/shared/gear-card/gear-image-carousel';
import { ProductDetailsModal } from '@/modules/players/v3/components/shared/gear-card/product-details-modal';
import { GEAR_CARD_HEIGHT, GEAR_CARD_WIDTH } from '@/modules/players/v3/constants/gear';
import { PlayerGear } from '@/modules/players/v3/types';
import { safeSanitizeHtml } from '@/modules/players/v3/utils/utils';

interface GearCardProps {
  gear: PlayerGear;
  disabled?: boolean;
  isWebview?: boolean;
  fluid?: boolean;
  isFavoritePending?: boolean;
  showActions?: boolean;
  onAddToFavorites: (playerGearId: string) => void;
  onRemoveFromFavorites: (playerGearId: string) => void;
  onDeleteGear: (playerGearId: string) => void;
}

export const GearCard = ({
  gear,
  disabled = false,
  isWebview,
  fluid = false,
  isFavoritePending = false,
  showActions = true,
  onAddToFavorites,
  onRemoveFromFavorites,
  onDeleteGear,
}: GearCardProps) => {
  const [showDetails, setShowDetails] = useState(false);
  const description = safeSanitizeHtml(gear.product.description);

  return (
    <div
      className={clsx(
        'relative flex select-none flex-col gap-2 overflow-hidden rounded-[13px] border border-gray-200 bg-white p-4 shadow-sm',
        fluid ? 'w-full' : 'mx-auto md:mx-0'
      )}
      style={
        fluid ? { height: GEAR_CARD_HEIGHT } : { width: GEAR_CARD_WIDTH, height: GEAR_CARD_HEIGHT }
      }
    >
      <div
        role='button'
        tabIndex={0}
        className='shrink-0 cursor-pointer'
        onClick={() => !disabled && setShowDetails(true)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            if (!disabled) setShowDetails(true);
          }
        }}
      >
        <GearImageCarousel
          images={gear.product.images}
          alt={gear.product.name}
          className='h-[130px] w-full'
          hideBadge
        />
      </div>

      <div
        role='button'
        tabIndex={0}
        className='flex min-h-0 flex-1 cursor-pointer flex-col gap-1'
        onClick={() => !disabled && setShowDetails(true)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            if (!disabled) setShowDetails(true);
          }
        }}
      >
        <p className='line-clamp-2 text-xs font-semibold text-gray-900'>{gear.product.name}</p>
        <p className='line-clamp-2 text-xs text-gray-600'>{description}</p>
      </div>

      <Button
        variant='secondary'
        size='xs'
        className='w-full justify-center'
        onClick={() => setShowDetails(true)}
      >
        <span className='mx-auto'>View</span>
      </Button>

      {showActions ? (
        <>
          <Button
            disabled={disabled || isFavoritePending}
            onClick={() =>
              gear.isFavorite ? onRemoveFromFavorites(gear.uuid) : onAddToFavorites(gear.uuid)
            }
            className='absolute left-2 top-2 z-10 size-6 bg-white shadow-sm'
            size='xs'
            variant='link'
            aria-label={gear.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FontAwesomeIcon
              icon={
                isFavoritePending ? faSpinnerThird : gear.isFavorite ? faHeartSolid : faHeartRegular
              }
              spin={isFavoritePending}
              className='mx-auto size-3'
            />
          </Button>
          <Button
            disabled={disabled}
            onClick={() => onDeleteGear(gear.uuid)}
            className='absolute right-2 top-2 z-10 size-6 bg-white shadow-sm'
            size='xs'
            variant='link-gray'
            aria-label='Delete gear'
          >
            <FontAwesomeIcon icon={faTrashCan} className='mx-auto size-3' />
          </Button>
        </>
      ) : null}

      <ProductDetailsModal
        gear={gear.product}
        open={showDetails}
        onOpenChange={setShowDetails}
        isWebview={isWebview}
      />
    </div>
  );
};
